import React, { useState, useContext } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import {
  Type, List, ImageIcon, Settings, Trash2, ArrowRight, Layout,
  Layers, Edit2, Plus, Lock, Unlock,
  AlignLeft, AlignCenter, AlignRight,
  AlignStartVertical, AlignCenterVertical, AlignEndVertical,
  ArrowUpDown, ArrowLeftRight, CheckCircle, MousePointer,
  Palette, ChevronDown, ChevronUp, Code, Info
} from 'lucide-react';
import RichTextEditor from '../RichTextEditor';
import NumericInput from './NumericInput';
import { ToggleRow } from '../../ui';
// import { getDefaultShape } from '../../Survey/BodyMap/geometry'; // Disabled with per-region edit UI
import { RESPONSE_OPTION_PRESETS, QUESTION_TYPE_DISPLAY } from './constants';
import { resolveOptionsLayout } from '../../../utils/responseOptionAppearance';
import { applySurveyButtonAction, resolveSurveyButtonAction } from '../../../utils/surveyButtonAction';
import { isNumericLike } from './utils';

// Button style presets
const BUTTON_STYLE_PRESETS = {
  primary: {
    name: 'Primary',
    backgroundColor: '#0d9488',
    textColor: '#ffffff',
    borderRadius: '8',
    borderWidth: '0',
    borderColor: '#0d9488'
  },
  secondary: {
    name: 'Secondary',
    backgroundColor: '#6b7280',
    textColor: '#ffffff',
    borderRadius: '8',
    borderWidth: '0',
    borderColor: '#6b7280'
  },
  success: {
    name: 'Success',
    backgroundColor: '#22c55e',
    textColor: '#ffffff',
    borderRadius: '8',
    borderWidth: '0',
    borderColor: '#22c55e'
  },
  danger: {
    name: 'Danger',
    backgroundColor: '#ef4444',
    textColor: '#ffffff',
    borderRadius: '8',
    borderWidth: '0',
    borderColor: '#ef4444'
  },
  outline: {
    name: 'Outline',
    backgroundColor: 'transparent',
    textColor: '#0d9488',
    borderRadius: '8',
    borderWidth: '2',
    borderColor: '#0d9488'
  },
  pill: {
    name: 'Pill',
    backgroundColor: '#0d9488',
    textColor: '#ffffff',
    borderRadius: '9999',
    borderWidth: '0',
    borderColor: '#0d9488'
  }
};

// Platform/Theme button styles
const BUTTON_THEME_PRESETS = {
  default: {
    name: 'Default',
    description: 'Standard web button',
    backgroundColor: '#0d9488',
    textColor: '#ffffff',
    borderRadius: '8',
    borderWidth: '0',
    borderColor: '#0d9488',
    fontWeight: '600'
  },
  ios: {
    name: 'iOS',
    description: 'Apple iOS style',
    backgroundColor: '#007AFF',
    textColor: '#ffffff',
    borderRadius: '12',
    borderWidth: '0',
    borderColor: '#007AFF',
    fontWeight: '600'
  },
  android: {
    name: 'Android / Material',
    description: 'Google Material Design',
    backgroundColor: '#1976D2',
    textColor: '#ffffff',
    borderRadius: '4',
    borderWidth: '0',
    borderColor: '#1976D2',
    fontWeight: '500'
  },
  windows: {
    name: 'Windows / Fluent',
    description: 'Microsoft Fluent Design',
    backgroundColor: '#0078D4',
    textColor: '#ffffff',
    borderRadius: '4',
    borderWidth: '0',
    borderColor: '#0078D4',
    fontWeight: '600'
  },
  macos: {
    name: 'macOS',
    description: 'Apple macOS style',
    backgroundColor: '#007AFF',
    textColor: '#ffffff',
    borderRadius: '6',
    borderWidth: '0',
    borderColor: '#007AFF',
    fontWeight: '500'
  },
  bootstrap: {
    name: 'Bootstrap',
    description: 'Bootstrap framework style',
    backgroundColor: '#0d6efd',
    textColor: '#ffffff',
    borderRadius: '6',
    borderWidth: '0',
    borderColor: '#0d6efd',
    fontWeight: '400'
  },
  flat: {
    name: 'Flat / Minimal',
    description: 'Clean flat design',
    backgroundColor: '#3498db',
    textColor: '#ffffff',
    borderRadius: '0',
    borderWidth: '0',
    borderColor: '#3498db',
    fontWeight: '600'
  },
  rounded: {
    name: 'Rounded / Soft',
    description: 'Soft rounded corners',
    backgroundColor: '#6366f1',
    textColor: '#ffffff',
    borderRadius: '24',
    borderWidth: '0',
    borderColor: '#6366f1',
    fontWeight: '500'
  },
  glassmorphism: {
    name: 'Glass / Modern',
    description: 'Glassmorphism effect',
    backgroundColor: 'rgba(255,255,255,0.2)',
    textColor: '#1f2937',
    borderRadius: '12',
    borderWidth: '1',
    borderColor: 'rgba(255,255,255,0.3)',
    fontWeight: '500'
  },
  neumorphism: {
    name: 'Neumorphic',
    description: 'Soft UI shadows',
    backgroundColor: '#e0e5ec',
    textColor: '#6b7280',
    borderRadius: '12',
    borderWidth: '0',
    borderColor: '#e0e5ec',
    fontWeight: '600'
  }
};

const FONT_FAMILY_OPTIONS = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: '"Times New Roman", serif', label: 'Times New Roman' },
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: '"Courier New", monospace', label: 'Courier New' },
  { value: 'system-ui, sans-serif', label: 'System UI' }
];

const FONT_WEIGHT_OPTIONS = [
  { value: '400', label: 'Normal' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'Semi-Bold' },
  { value: '700', label: 'Bold' }
];

// Which numeric weights each system font actually ships. System fonts
// (Arial, Times, Georgia, etc.) only have 400/700 — selecting 500 or 600
// silently falls back to 400, which is why Medium/Semi-Bold look identical
// to Normal on those fonts. system-ui resolves to Segoe UI Variable / SF Pro
// on modern OSes which support the full range.
const FONT_WEIGHT_SUPPORT = {
  'Arial, sans-serif': ['400', '700'],
  '"Times New Roman", serif': ['400', '700'],
  'Helvetica, Arial, sans-serif': ['400', '700'],
  'Georgia, serif': ['400', '700'],
  'Verdana, sans-serif': ['400', '700'],
  '"Courier New", monospace': ['400', '700'],
  'system-ui, sans-serif': ['400', '500', '600', '700'],
};

const TEXT_ALIGNMENT_OPTIONS = [
  { value: 'left', label: 'Left', icon: AlignLeft },
  { value: 'center', label: 'Center', icon: AlignCenter },
  { value: 'right', label: 'Right', icon: AlignRight }
];

const PropertiesPanel = ({
  selectedElementData,
  selectedElement,
  screens,
  currentScreenIndex,
  setScreens,
  setConfirmDialog,
  calculateQuestionHeight,
  updateElementContent,
  distributeOptions,
  alignOptions,
  toggleLockElement,
  isElementLocked,
  deleteElement,
  ungroupQuestion,
  regroupQuestion,
  updateSubElementPosition
}) => {
  const { user } = useContext(AuthContext);
  const [showAdvancedCss, setShowAdvancedCss] = useState(false);

  const updateElementStyle = (property, value) => {
    const updatedScreens = screens.map((screen, idx) => {
      if (idx !== currentScreenIndex) return screen;
      return {
        ...screen,
        elements: screen.elements.map(el =>
          el.id === selectedElement
            ? {
                ...el,
                style: { ...(el.style || {}), [property]: value }
              }
            : el
        )
      };
    });
    setScreens(updatedScreens);
  };

  // Helper to update button style properties
  const updateButtonStyle = (property, value) => {
    updateElementStyle(property, value);
  };

  // Apply a style preset to the button
  const applyStylePreset = (presetKey) => {
    const preset = BUTTON_STYLE_PRESETS[presetKey];
    if (!preset) return;
    
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { 
                  ...el, 
                  style: { 
                    ...(el.style || {}),
                    backgroundColor: preset.backgroundColor,
                    textColor: preset.textColor,
                    borderRadius: preset.borderRadius,
                    borderWidth: preset.borderWidth,
                    borderColor: preset.borderColor,
                    preset: presetKey
                  }
                }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  // Apply a theme preset to the button
  const applyThemePreset = (themeKey) => {
    const theme = BUTTON_THEME_PRESETS[themeKey];
    if (!theme) return;
    
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { 
                  ...el, 
                  style: { 
                    ...(el.style || {}),
                    backgroundColor: theme.backgroundColor,
                    textColor: theme.textColor,
                    borderRadius: theme.borderRadius,
                    borderWidth: theme.borderWidth,
                    borderColor: theme.borderColor,
                    fontWeight: theme.fontWeight,
                    theme: themeKey
                  }
                }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  if (!selectedElementData) {
    return (
      <div className="text-center mt-12 p-6">
        <div className="mb-3 flex justify-center">
          <Settings className="h-6 w-6 text-gray-400 dark:text-gray-500" />
        </div>
        <h4 className="mb-1 text-sm font-semibold text-gray-900 dark:text-gray-50">No Element Selected</h4>
        <p className="text-[13px] leading-relaxed text-gray-500 dark:text-gray-400">Click on an element in the canvas to edit its properties</p>
      </div>
    );
  }

  const handleAddOption = () => {
    const currentOptions = selectedElementData.responseOptions || [];
    const newOption = { 
      order: currentOptions.length + 1, 
      label: `Option ${currentOptions.length + 1}`, 
      value: currentOptions.length 
    };
    const updatedOptions = [...currentOptions, newOption];
    
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el => {
            if (el.id === selectedElement) {
              const newHeight = calculateQuestionHeight(
                { ...el.data, options: updatedOptions },
                el.responseConfig || {}
              );
              
              return { 
                ...el, 
                responseOptions: updatedOptions,
                data: {
                  ...el.data,
                  options: updatedOptions
                },
                size: {
                  ...el.size,
                  height: newHeight
                }
              };
            }
            return el;
          })
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  const handlePresetChange = (e) => {
    if (!e.target.value) return;
    
    const presetValue = e.target.value;
    const existingOptions = selectedElementData.responseOptions || [];
    
    const applyPreset = () => {
      const updatedScreens = screens.map((screen, idx) => {
        if (idx === currentScreenIndex) {
          return {
            ...screen,
            elements: screen.elements.map(el => {
              if (el.id === selectedElement) {
                const newHeight = calculateQuestionHeight(
                  { ...el.data, options: RESPONSE_OPTION_PRESETS[presetValue] },
                  el.responseConfig || {}
                );
                
                return { 
                  ...el, 
                  responseOptions: RESPONSE_OPTION_PRESETS[presetValue],
                  data: {
                    ...el.data,
                    options: RESPONSE_OPTION_PRESETS[presetValue]
                  },
                  size: {
                    ...el.size,
                    height: newHeight
                  }
                };
              }
              return el;
            })
          };
        }
        return screen;
      });
      setScreens(updatedScreens);
      e.target.value = '';
    };

    if (existingOptions.length > 0) {
      setConfirmDialog({
        isOpen: true,
        title: 'Replace Response Options?',
        message: `This will replace ${existingOptions.length} existing response option(s).\n\nAre you sure you want to continue?`,
        type: 'warning',
        confirmText: 'Replace',
        onConfirm: applyPreset
      });
      e.target.value = '';
    } else {
      applyPreset();
    }
  };

  const handleOptionLabelChange = (index, html) => {
    const updatedOptions = [...(selectedElementData.responseOptions || [])];
    updatedOptions[index].label = html;
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { 
                  ...el, 
                  responseOptions: updatedOptions,
                  data: {
                    ...el.data,
                    options: updatedOptions
                  }
                }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  const handleOptionValueChange = (index, value) => {
    const updatedOptions = [...(selectedElementData.responseOptions || [])];
    updatedOptions[index].value = Number(value);
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { 
                  ...el, 
                  responseOptions: updatedOptions,
                  data: {
                    ...el.data,
                    options: updatedOptions
                  }
                }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  const handleDeleteOption = (index) => {
    const updatedOptions = (selectedElementData.responseOptions || []).filter((_, i) => i !== index);
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el => {
            if (el.id === selectedElement) {
              const newHeight = calculateQuestionHeight(
                { ...el.data, options: updatedOptions },
                el.responseConfig || {}
              );
              
              return { 
                ...el, 
                responseOptions: updatedOptions,
                data: {
                  ...el.data,
                  options: updatedOptions
                },
                size: {
                  ...el.size,
                  height: newHeight
                }
              };
            }
            return el;
          })
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  const handleRequiredChange = (checked) => {
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { ...el, required: checked }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  const handleMultiSelectChange = (checked) => {
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { ...el, multiSelect: checked }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  const handleResponseConfigChange = (key, value) => {
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { ...el, responseConfig: { ...el.responseConfig, [key]: value } }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  // Compute where a group of given width should sit inside containerWidth
  // under a given alignment (left / center / right).
  const alignmentOffset = (align, containerWidth, groupWidth) => {
    if (align === 'center') return (containerWidth - groupWidth) / 2;
    if (align === 'right') return containerWidth - groupWidth;
    return 0;
  };

  const SCALE_SUB_TYPES = [
    'question-vas', 'question-nrs', 'question-vrs', 'question-segmented',
    'question-emoji', 'question-thermometer', 'question-stepped-slider', 'question-text-input'
  ];

  // Update responseConfig.optionsAlignment. If the element is ungrouped,
  // shift every option/scale sub by the delta between the old and new
  // aligned positions so Custom Layout visually matches grouped mode.
  const updateOptionsAlignment = (newAlignment) => {
    const el = selectedElementData;
    if (!el) return;
    const oldAlignment = el.responseConfig?.optionsAlignment ?? 'left';
    if (oldAlignment === newAlignment) return;

    const updatedScreens = screens.map((screen, idx) => {
      if (idx !== currentScreenIndex) return screen;
      return {
        ...screen,
        elements: screen.elements.map(e => {
          if (e.id !== selectedElement) return e;
          const nextConfig = { ...e.responseConfig, optionsAlignment: newAlignment };
          if (!e.isUngrouped || !Array.isArray(e.subElements)) {
            return { ...e, responseConfig: nextConfig };
          }
          // Identify the horizontal extent of the group being aligned.
          const groupSubs = e.subElements.filter(
            s => s.type === 'question-option' || SCALE_SUB_TYPES.includes(s.type)
          );
          if (groupSubs.length === 0) {
            return { ...e, responseConfig: nextConfig };
          }
          const minX = Math.min(...groupSubs.map(s => s.position.x));
          const maxX = Math.max(...groupSubs.map(s => s.position.x + s.size.width));
          const groupWidth = maxX - minX;
          const containerWidth = e.size?.width || 0;
          const oldOffset = alignmentOffset(oldAlignment, containerWidth, groupWidth);
          const newOffset = alignmentOffset(newAlignment, containerWidth, groupWidth);
          const delta = newOffset - oldOffset;
          if (delta === 0) {
            return { ...e, responseConfig: nextConfig };
          }
          const updatedSubs = e.subElements.map(sub => {
            if (sub.type === 'question-option' || SCALE_SUB_TYPES.includes(sub.type)) {
              return { ...sub, position: { ...sub.position, x: sub.position.x + delta } };
            }
            return sub;
          });
          return { ...e, responseConfig: nextConfig, subElements: updatedSubs };
        })
      };
    });
    setScreens(updatedScreens);
  };

  const handleElementCodeChange = (code) => {
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { ...el, code: code }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  const handleInputPropertyChange = (property, value) => {
    const updatedScreens = screens.map((screen, idx) => {
      if (idx === currentScreenIndex) {
        return {
          ...screen,
          elements: screen.elements.map(el =>
            el.id === selectedElement
              ? { ...el, [property]: value }
              : el
          )
        };
      }
      return screen;
    });
    setScreens(updatedScreens);
  };

  // Check if element is an input type that should have a code
  const isInputElement = ['text-input', 'number-input', 'date-picker', 'checkbox'].includes(selectedElementData?.type);
  const showTypographyControls = ['question', 'text', 'instruction'].includes(selectedElementData?.type);
  const responseLayout = selectedElementData.responseConfig?.layout || '';
  const showResponseFormattingControls = selectedElementData.type === 'question';
  const usesButtonOptions = ['outline', 'filled', 'solid'].includes(selectedElementData.responseConfig?.buttonStyle) ||
    (selectedElementData.responseConfig?.display || '').includes('button');
  const showRadioSizeControl = !['vas', 'nrs', 'vrs-unipolar', 'vrs-bipolar', 'text', 'dropdown'].includes(responseLayout) && !usesButtonOptions;

  return (
    <div className="space-y-4 p-4">
      {/* Element Info Card */}
      <div className="card p-4">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 text-gray-400 dark:text-gray-500">
            {selectedElementData.type === 'question' ? <List className="h-4 w-4" /> :
             selectedElementData.type === 'instruction' ? <Type className="h-4 w-4" /> :
             selectedElementData.type === 'text' ? <Type className="h-4 w-4" /> :
             selectedElementData.type === 'button' ? <ArrowRight className="h-4 w-4" /> :
             selectedElementData.type === 'image' ? <ImageIcon className="h-4 w-4" /> :
             <Layers className="h-4 w-4" />}
          </div>
          <div className="min-w-0 flex-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
              Element Type
            </label>
            <p className="text-base font-semibold capitalize text-gray-900 dark:text-gray-50">
              {selectedElementData.type.replace('-', ' ')}
              {selectedElementData.type === 'question' && (() => {
                const qType = selectedElementData.data?.type || selectedElementData.data?.response_type;
                const displayName = QUESTION_TYPE_DISPLAY[qType] || qType;
                return displayName ? <span className="font-normal text-gray-500 dark:text-gray-400"> — {displayName}</span> : null;
              })()}
            </p>
          </div>
          {selectedElementData.type === 'question' && selectedElementData.isLibraryItem && (
            <div className="flex flex-wrap items-center justify-end gap-1">
              <span className="badge badge-gray">
                <Lock className="h-3 w-3" />
                Library Item
              </span>
              {selectedElementData.isModified && (
                <span
                  title="This item has been edited since import"
                  className="badge badge-gray"
                >
                  <Edit2 className="h-3 w-3" />
                  Modified
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Question Settings - Required, Multi-select */}
      {selectedElementData.type === 'question' && (() => {
        const qLayout = selectedElementData.responseConfig?.layout;
        const qType = selectedElementData.data?.type || selectedElementData.itemData?.question_type;
        // Yes/no identity lives on the question type, not the layout — key off
        // both 'boolean' and 'yes_no' so multi-select stays hidden even after the
        // author switches the layout style away from the legacy 'boolean' marker.
        const hideMultiSelect = ['vas', 'nrs', 'text', 'boolean', 'vrs-unipolar', 'vrs-bipolar'].includes(qLayout) ||
          ['vas', 'numeric', 'text', 'boolean', 'yes_no', 'ordinal', 'likert', 'body_map'].includes(qType);
        return (
          <div className="card p-4">
            <div className="mb-3 flex items-center gap-2">
              <Settings className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
              <h4 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Question Settings</h4>
            </div>
            <div className="space-y-2">
              <ToggleRow
                title="Required"
                checked={selectedElementData.required || false}
                onChange={(e) => handleRequiredChange(e.target.checked)}
              />
              {!hideMultiSelect && (
                <ToggleRow
                  title="Allow Multiple Selection"
                  checked={selectedElementData.multiSelect || false}
                  onChange={(e) => handleMultiSelectChange(e.target.checked)}
                />
              )}
            </div>
          </div>
        );
      })()}

      {/* Body Map Layout Editor */}
      {selectedElementData.type === 'question' && selectedElementData.data?.type === 'body_map' && (() => {
        const overrides = selectedElementData.responseConfig?.regionOverrides || {};
        const editMode = !!selectedElementData.responseConfig?.bodyMapEditMode;

        const resetAll = () => {
          handleResponseConfigChange('regionOverrides', {});
          handleResponseConfigChange('bodyMapActiveSlug', null);
          handleResponseConfigChange('selectedPanelPosition', null);
        };

        // Per-region edit helpers disabled — kept for reference if region
        // editing is ever re-enabled.
        // const activeSlug = selectedElementData.responseConfig?.bodyMapActiveSlug || null;
        // const activeOverride = activeSlug ? overrides[activeSlug] : null;
        // const defaultShape = activeSlug ? getDefaultShape(activeSlug) : null;
        // const effective = activeSlug
        //   ? { ...(defaultShape || {}), ...(activeOverride || {}) }
        //   : null;
        //
        // const updateActiveRegion = (patch) => {
        //   if (!activeSlug) return;
        //   const nextRegion = { ...(overrides[activeSlug] || {}), ...patch };
        //   handleResponseConfigChange('regionOverrides', { ...overrides, [activeSlug]: nextRegion });
        // };
        //
        // const resetActiveRegion = () => {
        //   if (!activeSlug) return;
        //   const next = { ...overrides };
        //   delete next[activeSlug];
        //   handleResponseConfigChange('regionOverrides', next);
        // };
        //
        // const sizeFields = (() => {
        //   if (!effective) return [];
        //   switch (effective.type) {
        //     case 'circle':
        //       return [{ key: 'r', label: 'Radius' }];
        //     case 'rect':
        //       return [
        //         { key: 'w', label: 'Width' },
        //         { key: 'h', label: 'Height' },
        //       ];
        //     case 'ellipse':
        //       return [
        //         { key: 'rx', label: 'Width' },
        //         { key: 'ry', label: 'Height' },
        //       ];
        //     default:
        //       return [];
        //   }
        // })();

        return (
          <div className="card p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                <h4 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Body Map Layout</h4>
              </div>
              <button
                type="button"
                onClick={() => handleResponseConfigChange('bodyMapEditMode', !editMode)}
                className={`btn btn-sm ${
                  editMode ? 'btn-primary' : 'btn-secondary'
                }`}
              >
                {editMode ? 'Exit Edit Mode' : 'Edit Layout'}
              </button>
            </div>

            {editMode ? (
              <div className="space-y-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Drag the <strong>Selected Areas</strong> panel by its header to move it.
                </p>
                {/* Per-region edit UI disabled — individual body regions are no
                    longer draggable/resizable. Reset button retained below in
                    case existing overrides need to be cleared. */}
                {/* {activeSlug ? (
                  <div className="rounded-lg border border-gray-200/70 bg-gray-50 p-3 dark:border-white/[0.07] dark:bg-white/[0.03]">
                    <div className="mb-2"><span className="code-chip">{activeSlug}</span></div>
                    <div className="grid grid-cols-2 gap-2">
                      {effective?.cx !== undefined && (
                        <label className="text-[10px] text-gray-600 block">X
                          <NumericInput value={effective.cx} fallback={0} onChange={(v) => updateActiveRegion({ cx: v })} />
                        </label>
                      )}
                      {effective?.cy !== undefined && (
                        <label className="text-[10px] text-gray-600 block">Y
                          <NumericInput value={effective.cy} fallback={0} onChange={(v) => updateActiveRegion({ cy: v })} />
                        </label>
                      )}
                      {effective?.x !== undefined && (
                        <label className="text-[10px] text-gray-600 block">X
                          <NumericInput value={effective.x} fallback={0} onChange={(v) => updateActiveRegion({ x: v })} />
                        </label>
                      )}
                      {effective?.y !== undefined && (
                        <label className="text-[10px] text-gray-600 block">Y
                          <NumericInput value={effective.y} fallback={0} onChange={(v) => updateActiveRegion({ y: v })} />
                        </label>
                      )}
                      {sizeFields.map((f) => (
                        <label key={f.key} className="text-[10px] text-gray-600 block">{f.label}
                          <NumericInput
                            value={effective[f.key]}
                            fallback={0}
                            min={1}
                            onChange={(v) => updateActiveRegion({ [f.key]: v })}
                          />
                        </label>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={resetActiveRegion}
                      className="mt-3 text-[11px] text-gray-600 hover:text-gray-900 underline"
                    >
                      Reset this region to default
                    </button>
                  </div>
                ) : (
                  <p className="text-[11px] text-gray-400 italic">No region selected. Click one on the canvas.</p>
                )} */}
                <button
                  type="button"
                  onClick={resetAll}
                  className="btn btn-secondary btn-sm w-full"
                >
                  Reset entire layout
                </button>
              </div>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Toggle Edit Layout to reposition the <strong>Selected Areas</strong> panel.
                {Object.keys(overrides).length > 0 && ` (${Object.keys(overrides).length} region${Object.keys(overrides).length === 1 ? '' : 's'} customized)`}
              </p>
            )}
          </div>
        );
      })()}

      {/* Content & Formatting - Hidden for library items */}
      {(selectedElementData.type === 'text' || selectedElementData.type === 'button' || (selectedElementData.type === 'question' && !selectedElementData.isLibraryItem)) && (
        <div className="card p-4">
          <div className="mb-3 flex items-center gap-2">
            <Edit2 className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <h4 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">
              {selectedElementData.type === 'text' ? 'Content' :
               selectedElementData.type === 'button' ? 'Button Text' : 'Question Text'}
            </h4>
          </div>
          <RichTextEditor
            value={selectedElementData.type === 'text'
              ? selectedElementData.content
              : selectedElementData.type === 'button'
              ? (selectedElementData.content || selectedElementData.label || '')
              : (selectedElementData.content || selectedElementData.itemData?.text || '')
            }
            onChange={(html) => updateElementContent(selectedElement, html)}
            placeholder={selectedElementData.type === 'text'
              ? 'Enter text content...'
              : selectedElementData.type === 'button'
              ? 'Enter button text...'
              : 'Enter question text...'
            }
            rows={3}
          />
        </div>
      )}

      {showTypographyControls && (
        <div className="card p-4">
          <div className="mb-3 flex items-center gap-2">
            <Type className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <h4 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Typography &amp; Formatting</h4>
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Font Family</label>
              <select
                value={selectedElementData.style?.fontFamily || 'Arial, sans-serif'}
                onChange={(e) => updateElementStyle('fontFamily', e.target.value)}
                className="input h-9"
              >
                {FONT_FAMILY_OPTIONS.map((option) => (
                  <option key={option.label} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-gray-400">Font Size</label>
                <p className="mb-1 text-[11px] text-gray-500 dark:text-gray-400">Question stem text size</p>
                <div className="flex items-center gap-1.5">
                  <NumericInput
                    min={8}
                    max={72}
                    value={selectedElementData.style?.fontSize ?? 16}
                    fallback={16}
                    onChange={(val) => updateElementStyle('fontSize', val)}
                    className="input h-9 w-full min-w-0 tabular-nums"
                  />
                  <span className="text-xs text-gray-500 dark:text-gray-400">px</span>
                </div>
              </div>

              <div>
                <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-gray-400">Line Height</label>
                <p className="mb-1 text-[11px] text-gray-500 dark:text-gray-400">Multiplier (1.0 = tight, 2.0 = double)</p>
                <NumericInput
                  float
                  min={1}
                  max={3}
                  step="0.1"
                  fallback={1.5}
                  value={selectedElementData.style?.lineHeight ?? 1.5}
                  onChange={(val) => updateElementStyle('lineHeight', val)}
                  className="input h-9 w-full tabular-nums"
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Font Weight</label>
              <div className="grid grid-cols-2 gap-2">
                {(() => {
                  const currentFont = selectedElementData.style?.fontFamily || 'Arial, sans-serif';
                  const supported = FONT_WEIGHT_SUPPORT[currentFont] || FONT_WEIGHT_OPTIONS.map(o => o.value);
                  return FONT_WEIGHT_OPTIONS
                    .filter(option => supported.includes(option.value))
                    .map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => updateElementStyle('fontWeight', option.value)}
                        className={`rounded-lg border px-3 py-2 text-[13px] font-medium transition-colors ${
                          String(selectedElementData.style?.fontWeight || '400') === option.value
                            ? 'border-primary-500 bg-primary-50 text-primary-700 dark:border-primary-400 dark:bg-primary-400/10 dark:text-primary-300'
                            : 'border-gray-200/80 bg-white text-gray-700 hover:border-gray-300 dark:border-white/10 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-white/20'
                        }`}
                      >
                        {option.label}
                      </button>
                    ));
                })()}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                {selectedElementData.type === 'question' ? 'Question Text Alignment' : 'Text Alignment'}
              </label>
              <div className="segmented flex w-full">
                {TEXT_ALIGNMENT_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => updateElementStyle('textAlign', option.value)}
                      className={`segmented-item flex-1 justify-center px-2 ${
                        (selectedElementData.style?.textAlign || 'left') === option.value
                          ? 'segmented-item-active'
                          : ''
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Response Options Alignment — only for question elements */}
            {selectedElementData.type === 'question' && (
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Response Options Alignment</label>
                <div className="segmented flex w-full">
                  {TEXT_ALIGNMENT_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const current = selectedElementData.responseConfig?.optionsAlignment ?? 'left';
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => updateOptionsAlignment(option.value)}
                        className={`segmented-item flex-1 justify-center px-2 ${
                          current === option.value
                            ? 'segmented-item-active'
                            : ''
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Text Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={selectedElementData.style?.color || '#111827'}
                  onChange={(e) => updateElementStyle('color', e.target.value)}
                  className="h-9 w-9 flex-shrink-0 cursor-pointer rounded-lg border border-gray-300/80 dark:border-white/10"
                />
                <input
                  type="text"
                  value={selectedElementData.style?.color || '#111827'}
                  onChange={(e) => updateElementStyle('color', e.target.value)}
                  className="input h-9 min-w-0 flex-1 font-mono"
                  placeholder="#111827"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Response Options - Hidden for library items and types with fixed option sets (body_map) */}
      {selectedElementData.type === 'question' && !selectedElementData.isLibraryItem && selectedElementData.data?.type !== 'body_map' && (
        <div className="card overflow-hidden p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">
                <List className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                Response Options
              </label>
              {(selectedElementData.itemData?.options || selectedElementData.itemData?.response_options) && (
                <span className="badge badge-gray">
                  Imported
                </span>
              )}
              {(selectedElementData.responseOptions?.length || 0) > 0 && (
                <span className="badge badge-gray tabular-nums">
                  {selectedElementData.responseOptions.length} {selectedElementData.responseOptions.length === 1 ? 'option' : 'options'}
                </span>
              )}
            </div>
            <button onClick={handleAddOption} className="btn btn-secondary btn-sm">
              <Plus className="h-3.5 w-3.5" />
              Add Option
            </button>
          </div>
          <div className="mb-4">
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Quick Fill</label>
            <select className="input h-9" onChange={handlePresetChange}>
              <option value="">-- Select Preset --</option>
              <option value="phq9">PHQ-9 Scale (0-3)</option>
              <option value="gad7">GAD-7 Scale (0-3)</option>
              <option value="nrs">NRS 0-10</option>
              <option value="severity">Severity (None to Severe)</option>
              <option value="likert5">Ordinal 5-Point</option>
              <option value="yesno">Yes/No</option>
            </select>
          </div>
          <div className="relative overflow-x-auto overflow-y-visible scroll-smooth rounded-lg border border-gray-200/80 dark:border-white/[0.07]">
            <table className="w-full text-xs min-w-[400px]">
              <thead className="border-b border-gray-100 bg-gray-50/80 dark:border-white/[0.06] dark:bg-white/[0.02]">
                <tr>
                  <th className="w-10 min-w-[40px] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">#</th>
                  <th className="min-w-[180px] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Label</th>
                  <th className="w-20 min-w-[80px] px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Value</th>
                  <th className="w-12 min-w-[48px] px-2 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400"></th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800">
                {(selectedElementData.responseOptions || []).map((option, index) => (
                  <tr key={index} className="group border-b border-gray-100 transition-colors last:border-b-0 hover:bg-gray-50/70 dark:border-white/[0.06] dark:hover:bg-white/[0.03]">
                    <td className="px-2 py-2.5">
                      <div className="flex h-6 w-6 items-center justify-center text-xs font-medium tabular-nums text-gray-500 dark:text-gray-400">{option.order}</div>
                    </td>
                    <td className="px-2 py-2">
                      <RichTextEditor value={option.label} onChange={(html) => handleOptionLabelChange(index, html)} compact={true} />
                    </td>
                    <td className="px-2 py-2">
                      <NumericInput value={option.value} fallback={0} onChange={(val) => handleOptionValueChange(index, val)} className="input h-9 w-full px-2 text-center tabular-nums" />
                    </td>
                    <td className="px-2 py-2 text-center">
                      <button onClick={() => handleDeleteOption(index)} className="btn btn-danger-soft btn-sm btn-icon opacity-0 group-hover:opacity-100 focus-visible:opacity-100" title="Delete option">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {(!selectedElementData.responseOptions || selectedElementData.responseOptions.length === 0) && (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <List className="mb-2 h-6 w-6 text-gray-400 dark:text-gray-500" />
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">No response options yet</p>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Click "+ Add Option" to define choices.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-start gap-1.5 mt-2 px-1">
            <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">
              <span className="font-medium text-gray-700 dark:text-gray-300">Values</span> are used for scoring calculations and skip logic evaluations
            </p>
          </div>
        </div>
      )}

      {/* Response Display (for questions with response options, or scale/slider types without options) */}
      {selectedElementData.type === 'question' && (() => {
        const qType = selectedElementData.data?.type || selectedElementData.data?.response_type;
        const qLayout = selectedElementData.responseConfig?.layout;
        const isScaleType = ['vas', 'nrs', 'numeric'].includes(qType) ||
          ['vas', 'nrs', 'vrs-unipolar', 'vrs-bipolar'].includes(qLayout);
        return selectedElementData.responseOptions?.length > 0 || isScaleType;
      })() && (
        <div className="card p-4">
          <div className="mb-3 flex items-center gap-2">
            <Layout className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <h4 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Response Layout</h4>
          </div>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Layout Style</label>
              {(() => {
                const questionType = selectedElementData.data?.type || selectedElementData.data?.response_type || 'ordinal';
                const handleLayoutChange = (e) => {
                  const newLayout = e.target.value;
                  const updatedConfig = { ...selectedElementData.responseConfig, layout: newLayout };
                  const newHeight = calculateQuestionHeight(selectedElementData.data, updatedConfig);
                  const updatedScreens = screens.map((screen, idx) => {
                    if (idx === currentScreenIndex) {
                      return {
                        ...screen,
                        elements: screen.elements.map(el =>
                          el.id === selectedElement
                            ? { ...el, responseConfig: updatedConfig, size: { ...el.size, height: newHeight } }
                            : el
                        )
                      };
                    }
                    return screen;
                  });
                  setScreens(updatedScreens);
                };
                return (
                  <select
                    value={
                      (questionType === 'nrs' || questionType === 'numeric') && (!selectedElementData.responseConfig?.layout || selectedElementData.responseConfig?.layout === 'text' || selectedElementData.responseConfig?.layout === 'vertical')
                        ? 'nrs'
                        // `boolean` (yes/no) is a type marker, not a selectable visual
                        // layout — resolveOptionsLayout surfaces it as "Vertical List"
                        // so the control reflects the real state instead of silently
                        // mismatching (which forced a display toggle before alignment
                        // and other settings would apply) (FECOA yes/no parity fix).
                        : resolveOptionsLayout(selectedElementData.responseConfig?.layout) || 'vertical'
                    }
                    onChange={handleLayoutChange}
                    className="input h-9"
                  >
                    {questionType === 'vas' ? (
                      <option value="vas">VAS - Visual Analog Scale (Slider)</option>
                    ) : questionType === 'body_map' ? (
                      <option value="body_map">Body Map (Front + Back)</option>
                    ) : questionType === 'nrs' || questionType === 'numeric' ? (
                      <optgroup label="Numeric Scale Layouts">
                        <option value="nrs">NRS - Numeric Rating Scale</option>
                        <option value="thermometer">Thermometer Scale</option>
                      </optgroup>
                    ) : questionType === 'text' || questionType === 'free_text' ? (
                      <option value="textarea">Text Area</option>
                    ) : questionType === 'dropdown' ? (
                      <option value="dropdown">Dropdown Select</option>
                    ) : questionType === 'yes_no' || questionType === 'boolean' ? (
                      <optgroup label="Yes/No Layouts">
                        <option value="horizontal">Horizontal Buttons</option>
                        <option value="vertical">Vertical List</option>
                      </optgroup>
                    ) : questionType === 'ordinal' || questionType === 'likert' ? (
                      <>
                        <optgroup label="Standard">
                          <option value="vertical">Vertical List</option>
                          <option value="horizontal">Horizontal Buttons</option>
                          <option value="grid">Grid Layout</option>
                        </optgroup>
                        {user?.is_superuser && (
                          <optgroup label="Clinical Scales">
                            <option value="segmented">Segmented Control</option>
                            <option value="emoji">Emoji / Face Scale</option>
                            <option value="stepped-slider">Stepped Slider</option>
                            <option value="thermometer">Thermometer Scale</option>
                          </optgroup>
                        )}
                      </>
                    ) : questionType === 'rating' ? (
                      <>
                        <optgroup label="Scale Layouts">
                          <option value="vas">VAS - Visual Analog Scale (Slider)</option>
                          <option value="nrs">NRS - Numeric Rating Scale (0-10)</option>
                          <option value="vrs-unipolar">VRS - Verbal Rating Scale (Unipolar)</option>
                          <option value="vrs-bipolar">VRS - Verbal Rating Scale (Bipolar)</option>
                        </optgroup>
                        <optgroup label="Button Layouts">
                          <option value="horizontal">Horizontal Buttons</option>
                          <option value="vertical">Vertical List</option>
                        </optgroup>
                      </>
                    ) : questionType === 'checkbox' ? (
                      <optgroup label="Checkbox Layouts">
                        <option value="vertical">Vertical List</option>
                        <option value="grid">Grid Layout</option>
                      </optgroup>
                    ) : (
                      <optgroup label="Standard Layouts">
                        <option value="vertical">Vertical List</option>
                        <option value="horizontal">Horizontal Buttons</option>
                        <option value="grid">Grid Layout</option>
                      </optgroup>
                    )}
                  </select>
                );
              })()}
            </div>

            {selectedElementData.responseConfig?.layout === 'grid' && (
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Grid Columns</label>
                <select
                  value={selectedElementData.responseConfig?.gridColumns || 2}
                  onChange={(e) => handleResponseConfigChange('gridColumns', parseInt(e.target.value))}
                  className="input h-9"
                >
                  <option value={2}>2 Columns</option>
                  <option value={3}>3 Columns</option>
                  <option value={4}>4 Columns</option>
                </select>
              </div>
            )}

            {/* VAS Configuration */}
            {selectedElementData.responseConfig?.layout === 'vas' && (
              <div className="space-y-3 border-t border-gray-100 pt-3 dark:border-white/[0.06]">
                <div className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">VAS Settings</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Left Anchor</label>
                    <input type="text" value={selectedElementData.responseConfig?.leftAnchor || ''} onChange={(e) => handleResponseConfigChange('leftAnchor', e.target.value)} placeholder="e.g., No pain" className="input h-9" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Right Anchor</label>
                    <input type="text" value={selectedElementData.responseConfig?.rightAnchor || ''} onChange={(e) => handleResponseConfigChange('rightAnchor', e.target.value)} placeholder="e.g., Worst pain" className="input h-9" />
                  </div>
                </div>
                <ToggleRow
                  title="Show selected value under slider"
                  checked={selectedElementData.responseConfig?.showCurrentValue || false}
                  onChange={(e) => handleResponseConfigChange('showCurrentValue', e.target.checked)}
                />
                <ToggleRow
                  title="Color gradient (green to red)"
                  checked={selectedElementData.responseConfig?.colorGradient || false}
                  onChange={(e) => handleResponseConfigChange('colorGradient', e.target.checked)}
                />
              </div>
            )}

            {/* Numeric Scale Configuration — shared by NRS and Thermometer.
                Both need min/max and anchor labels; orientation and color
                gradient are NRS-only (thermometer is always vertical and uses
                a single fillColor). */}
            {(selectedElementData.responseConfig?.layout === 'nrs' || selectedElementData.responseConfig?.layout === 'thermometer' || isNumericLike(selectedElementData.data?.type)) && (() => {
              const isThermometer = selectedElementData.responseConfig?.layout === 'thermometer';
              return (
              <div className="space-y-3 border-t border-gray-100 pt-3 dark:border-white/[0.06]">
                <div className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">
                  {isThermometer ? 'Thermometer Settings' : 'NRS Settings'}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Min Value</label>
                    <NumericInput value={selectedElementData.responseConfig?.minValue ?? 0} fallback={0} onChange={(val) => handleResponseConfigChange('minValue', val)} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Max Value</label>
                    <NumericInput value={selectedElementData.responseConfig?.maxValue ?? 10} fallback={10} onChange={(val) => handleResponseConfigChange('maxValue', val)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">{isThermometer ? 'Bottom Anchor' : 'Left Anchor'}</label>
                    <input type="text" value={selectedElementData.responseConfig?.leftAnchor || ''} onChange={(e) => handleResponseConfigChange('leftAnchor', e.target.value)} placeholder={isThermometer ? 'e.g., Worst' : 'e.g., No pain'} className="input h-9" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">{isThermometer ? 'Top Anchor' : 'Right Anchor'}</label>
                    <input type="text" value={selectedElementData.responseConfig?.rightAnchor || ''} onChange={(e) => handleResponseConfigChange('rightAnchor', e.target.value)} placeholder={isThermometer ? 'e.g., Best' : 'e.g., Worst pain'} className="input h-9" />
                  </div>
                </div>
                {/* Anchor placement — horizontal NRS only. A label centered on
                    its end tick can only be as wide as one column before it
                    wraps, so long anchors need one of the roomier options. */}
                {!isThermometer && (selectedElementData.responseConfig?.orientation || 'horizontal') === 'horizontal' && (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Anchor Placement</label>
                    <select
                      value={selectedElementData.responseConfig?.anchorPlacement || 'below'}
                      onChange={(e) => handleResponseConfigChange('anchorPlacement', e.target.value)}
                      className="input h-9"
                    >
                      <option value="below">Under end numbers (centered)</option>
                      <option value="below-edges">Under scale edges (wider)</option>
                      <option value="sides">Beside the scale (one line)</option>
                      <option value="custom">Custom position</option>
                    </select>
                    <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                      Centered keeps each label under its number and wraps long text.
                      Edges and Beside give more room for a long anchor on one line.
                      Custom lets you place each one exactly.
                    </p>
                    {selectedElementData.responseConfig?.anchorPlacement === 'custom' && (
                      <div className="mt-2 space-y-2 rounded-lg border border-gray-200/70 bg-gray-50 dark:border-white/[0.07] dark:bg-white/[0.03] p-3">
                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                          Pixels from the ends of the scale, where 0,0 sits flush.
                          Move the right anchor inward with a negative X, the left
                          anchor inward with a positive X. Negative Y moves up.
                        </p>
                        {[
                          { side: 'left', label: 'Left anchor' },
                          { side: 'right', label: 'Right anchor' },
                        ].map(({ side, label }) => (
                          <div key={side}>
                            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">{label} offset</label>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="flex items-center gap-1.5">
                                <span className="w-3 text-xs text-gray-500 dark:text-gray-400">X</span>
                                <NumericInput
                                  value={selectedElementData.responseConfig?.[`${side}AnchorX`] ?? 0}
                                  fallback={0}
                                  onChange={(val) => handleResponseConfigChange(`${side}AnchorX`, val)}
                                />
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="w-3 text-xs text-gray-500 dark:text-gray-400">Y</span>
                                <NumericInput
                                  value={selectedElementData.responseConfig?.[`${side}AnchorY`] ?? 0}
                                  fallback={0}
                                  onChange={(val) => handleResponseConfigChange(`${side}AnchorY`, val)}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {isThermometer ? (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Step</label>
                    <NumericInput value={selectedElementData.responseConfig?.step ?? 10} fallback={10} onChange={(val) => handleResponseConfigChange('step', val)} />
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Orientation</label>
                      <select value={selectedElementData.responseConfig?.orientation || 'horizontal'} onChange={(e) => handleResponseConfigChange('orientation', e.target.value)} className="input h-9">
                        <option value="horizontal">Horizontal</option>
                        <option value="vertical">Vertical</option>
                      </select>
                    </div>
                    <ToggleRow
                      title="Color gradient (green to red)"
                      checked={selectedElementData.responseConfig?.colorGradient || false}
                      onChange={(e) => handleResponseConfigChange('colorGradient', e.target.checked)}
                    />
                  </>
                )}
              </div>
              );
            })()}

            {/* VRS Configuration */}
            {(selectedElementData.responseConfig?.layout === 'vrs-unipolar' || selectedElementData.responseConfig?.layout === 'vrs-bipolar') && (
              <div className="space-y-3 border-t border-gray-100 pt-3 dark:border-white/[0.06]">
                <div className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">VRS Settings</div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Orientation</label>
                  <select value={selectedElementData.responseConfig?.orientation || 'vertical'} onChange={(e) => {
                    const updatedConfig = { ...selectedElementData.responseConfig, orientation: e.target.value };
                    const newHeight = calculateQuestionHeight(selectedElementData.data, updatedConfig);
                    const updatedScreens = screens.map((screen, idx) => {
                      if (idx === currentScreenIndex) {
                        return { ...screen, elements: screen.elements.map(el => el.id === selectedElement ? { ...el, responseConfig: updatedConfig, size: { ...el.size, height: newHeight } } : el) };
                      }
                      return screen;
                    });
                    setScreens(updatedScreens);
                  }} className="input h-9">
                    <option value="vertical">Vertical</option>
                    <option value="horizontal">Horizontal</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Button Spacing (px)</label>
                  <NumericInput value={selectedElementData.responseConfig?.spacing || 12} fallback={12} min={0} onChange={(val) => handleResponseConfigChange('spacing', val)} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Button Height (px)</label>
                    <NumericInput value={selectedElementData.responseConfig?.vrsButtonHeight || 48} fallback={48} min={20} onChange={(val) => handleResponseConfigChange('vrsButtonHeight', val)} />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Min Width (px)</label>
                    <NumericInput value={selectedElementData.responseConfig?.vrsButtonMinWidth || 200} fallback={200} min={50} onChange={(val) => handleResponseConfigChange('vrsButtonMinWidth', val)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Selected Color</label>
                    <input type="color" value={selectedElementData.responseConfig?.vrsSelectedColor || '#0d9488'} onChange={(e) => handleResponseConfigChange('vrsSelectedColor', e.target.value)} className="h-9 w-full cursor-pointer rounded-lg border border-gray-300/80 dark:border-white/10" />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Unselected Color</label>
                    <input type="color" value={selectedElementData.responseConfig?.vrsUnselectedColor || '#ffffff'} onChange={(e) => handleResponseConfigChange('vrsUnselectedColor', e.target.value)} className="h-9 w-full cursor-pointer rounded-lg border border-gray-300/80 dark:border-white/10" />
                  </div>
                </div>
              </div>
            )}

            {/* Option Appearance — for standard layouts (vertical, horizontal, grid).
                Suppressed for numeric-type questions since they render as the NRS
                scale regardless of responseConfig.layout, so these controls
                (gap / equal-width / font-size / padding) would have no visible
                effect and confuse the author. */}
            {['vertical', 'horizontal', 'grid'].includes(resolveOptionsLayout(selectedElementData.responseConfig?.layout)) && selectedElementData.data?.type !== 'numeric' && (
              <div className="space-y-3 border-t border-gray-100 pt-3 dark:border-white/[0.06]">
                <div className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Option Appearance</div>

                {/* Display mode: radio/checkbox vs buttons */}
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Display As</label>
                  <select
                    value={
                      selectedElementData.responseConfig?.buttonStyle
                        ? selectedElementData.responseConfig.buttonStyle
                        : 'radio'
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === 'radio') {
                        handleResponseConfigChange('buttonStyle', null);
                        return;
                      }
                      const defaults = val === 'filled'
                        ? { btnBgColor: '#0d9488', btnTextColor: '#ffffff', btnBorderColor: '#0d9488' }
                        : { btnBgColor: '#ffffff', btnTextColor: '#0d9488', btnBorderColor: '#0d9488' };
                      const updatedScreens = screens.map((screen, idx) => {
                        if (idx !== currentScreenIndex) return screen;
                        return {
                          ...screen,
                          elements: screen.elements.map(el =>
                            el.id === selectedElement
                              ? { ...el, responseConfig: { ...el.responseConfig, buttonStyle: val, ...defaults } }
                              : el
                          )
                        };
                      });
                      setScreens(updatedScreens);
                    }}
                    className="input h-9"
                  >
                    <option value="radio">Radio Buttons</option>
                    <option value="outline">Outlined Buttons</option>
                    <option value="filled">Filled Buttons</option>
                  </select>
                </div>

                {/* Equal width toggle — only for button modes or vertical layout */}
                <ToggleRow
                  title="Equal width options"
                  checked={selectedElementData.responseConfig?.equalWidth || false}
                  onChange={(e) => handleResponseConfigChange('equalWidth', e.target.checked)}
                />

                {/* Option spacing */}
                <div>
                  <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-gray-400">Gap Between Options (px)</label>
                  <p className="mb-1 text-[11px] text-gray-500 dark:text-gray-400">Vertical space between each option row</p>
                  <NumericInput
                    min={0}
                    max={32}
                    value={selectedElementData.responseConfig?.spacing ?? 8}
                    fallback={8}
                    onChange={(val) => handleResponseConfigChange('spacing', val)}
                  />
                </div>

                {/* Option font size + padding — always visible */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-gray-400">Option Font Size (px)</label>
                    <p className="mb-1 text-[11px] text-gray-500 dark:text-gray-400">Response option text size</p>
                    <NumericInput
                      min={8}
                      max={32}
                      value={selectedElementData.responseConfig?.optionFontSize ?? 14}
                      fallback={14}
                      onChange={(val) => handleResponseConfigChange('optionFontSize', val)}
                    />
                  </div>
                  <div>
                    <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-gray-400">Inner Padding (px)</label>
                    <p className="mb-1 text-[11px] text-gray-500 dark:text-gray-400">Space inside each option</p>
                    <NumericInput
                      min={2}
                      max={24}
                      value={selectedElementData.responseConfig?.optionPadding ?? 8}
                      fallback={8}
                      onChange={(val) => handleResponseConfigChange('optionPadding', val)}
                    />
                  </div>
                </div>

                {/* Radio/Checkbox size — only when not using button style */}
                {showRadioSizeControl && (
                  <div>
                    <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-gray-400">Radio / Checkbox Size (px)</label>
                    <p className="mb-1 text-[11px] text-gray-500 dark:text-gray-400">Diameter of the indicator dot/box</p>
                    <NumericInput
                      min={12}
                      max={32}
                      value={selectedElementData.responseConfig?.radioSize ?? 16}
                      fallback={16}
                      onChange={(val) => handleResponseConfigChange('radioSize', val)}
                    />
                  </div>
                )}

                {/* Button styling controls — only when using button display modes */}
                {usesButtonOptions && (
                  <div className="space-y-3 border-t border-gray-100 pt-3 dark:border-white/[0.06]">
                    <div className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Button Styling</div>

                    {/* Box width and text alignment */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-gray-400">Box Width (px)</label>
                        <p className="mb-1 text-[11px] text-gray-500 dark:text-gray-400">Use 0 for automatic width</p>
                        <NumericInput
                          min={0}
                          max={600}
                          value={selectedElementData.responseConfig?.optionBoxWidth ?? 0}
                          fallback={0}
                          onChange={(val) => handleResponseConfigChange('optionBoxWidth', val)}
                        />
                      </div>
                      <div>
                        <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-gray-400">Text Align</label>
                        <p className="mb-1 text-[11px] text-gray-500 dark:text-gray-400">Inside each option box</p>
                        <select
                          value={selectedElementData.responseConfig?.optionTextAlign || 'center'}
                          onChange={(e) => handleResponseConfigChange('optionTextAlign', e.target.value)}
                          className="input h-9"
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                        </select>
                      </div>
                    </div>

                    {/* Box height — pairs with Box Width above */}
                    <div>
                      <label className="mb-0.5 block text-xs font-medium text-gray-600 dark:text-gray-400">Box Height (px)</label>
                      <p className="mb-1 text-[11px] text-gray-500 dark:text-gray-400">Use 0 for automatic height. Sets a fixed height and centers the label vertically.</p>
                      <NumericInput
                        min={0}
                        max={200}
                        value={selectedElementData.responseConfig?.optionBoxHeight ?? 0}
                        fallback={0}
                        onChange={(val) => handleResponseConfigChange('optionBoxHeight', val)}
                      />
                    </div>

                    {/* Border radius and width */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Border Radius (px)</label>
                        <NumericInput
                          min={0}
                          max={24}
                          value={selectedElementData.responseConfig?.btnBorderRadius ?? 4}
                          fallback={4}
                          onChange={(val) => handleResponseConfigChange('btnBorderRadius', val)}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Border Width (px)</label>
                        <NumericInput
                          min={1}
                          max={4}
                          value={selectedElementData.responseConfig?.btnBorderWidth ?? 1}
                          fallback={1}
                          onChange={(val) => handleResponseConfigChange('btnBorderWidth', val)}
                        />
                      </div>
                    </div>

                    {/* Unselected colors */}
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Unselected State</label>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="mb-0.5 block text-[11px] text-gray-500 dark:text-gray-400">Background</label>
                          <input type="color" value={selectedElementData.responseConfig?.btnBgColor || '#ffffff'} onChange={(e) => handleResponseConfigChange('btnBgColor', e.target.value)} className="h-8 w-full cursor-pointer rounded-lg border border-gray-300/80 dark:border-white/10" />
                        </div>
                        <div>
                          <label className="mb-0.5 block text-[11px] text-gray-500 dark:text-gray-400">Text</label>
                          <input type="color" value={selectedElementData.responseConfig?.btnTextColor || '#374151'} onChange={(e) => handleResponseConfigChange('btnTextColor', e.target.value)} className="h-8 w-full cursor-pointer rounded-lg border border-gray-300/80 dark:border-white/10" />
                        </div>
                        <div>
                          <label className="mb-0.5 block text-[11px] text-gray-500 dark:text-gray-400">Border</label>
                          <input type="color" value={selectedElementData.responseConfig?.btnBorderColor || '#d1d5db'} onChange={(e) => handleResponseConfigChange('btnBorderColor', e.target.value)} className="h-8 w-full cursor-pointer rounded-lg border border-gray-300/80 dark:border-white/10" />
                        </div>
                      </div>
                    </div>

                    {/* Selected colors */}
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Selected State</label>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="mb-0.5 block text-[11px] text-gray-500 dark:text-gray-400">Background</label>
                          <input type="color" value={selectedElementData.responseConfig?.btnSelectedBgColor || '#0d9488'} onChange={(e) => handleResponseConfigChange('btnSelectedBgColor', e.target.value)} className="h-8 w-full cursor-pointer rounded-lg border border-gray-300/80 dark:border-white/10" />
                        </div>
                        <div>
                          <label className="mb-0.5 block text-[11px] text-gray-500 dark:text-gray-400">Text</label>
                          <input type="color" value={selectedElementData.responseConfig?.btnSelectedTextColor || '#ffffff'} onChange={(e) => handleResponseConfigChange('btnSelectedTextColor', e.target.value)} className="h-8 w-full cursor-pointer rounded-lg border border-gray-300/80 dark:border-white/10" />
                        </div>
                        <div>
                          <label className="mb-0.5 block text-[11px] text-gray-500 dark:text-gray-400">Border</label>
                          <input type="color" value={selectedElementData.responseConfig?.btnSelectedBorderColor || '#0d9488'} onChange={(e) => handleResponseConfigChange('btnSelectedBorderColor', e.target.value)} className="h-8 w-full cursor-pointer rounded-lg border border-gray-300/80 dark:border-white/10" />
                        </div>
                      </div>
                    </div>

                    {/* Font weight */}
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Font Weight</label>
                      <select
                        value={selectedElementData.responseConfig?.btnFontWeight || '400'}
                        onChange={(e) => handleResponseConfigChange('btnFontWeight', e.target.value)}
                        className="input h-9"
                      >
                        <option value="300">Light</option>
                        <option value="400">Normal</option>
                        <option value="500">Medium</option>
                        <option value="600">Semibold</option>
                        <option value="700">Bold</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Free Position Mode — ungroup/regroup toggle */}
            {ungroupQuestion && regroupQuestion && (
              <div className="space-y-2 border-t border-gray-100 pt-3 dark:border-white/[0.06]">
                {selectedElementData.isUngrouped ? (
                  <>
                    <button
                      onClick={() => regroupQuestion(selectedElement)}
                      className="btn btn-secondary btn-sm w-full"
                    >
                      <Layers className="w-3.5 h-3.5" />
                      Exit Free Position
                    </button>

                    {(() => {
                      const pad = selectedElementData.style?.padding || 0;
                      const parentW = selectedElementData.size.width - pad * 2;
                      const parentH = selectedElementData.size.height - pad * 2;
                      const textSub = selectedElementData.subElements?.find(s => s.type === 'question-text');
                      const options = selectedElementData.subElements?.filter(s => s.type === 'question-option') || [];
                      const spacing = selectedElementData.responseConfig?.spacing || 8;
                      const optFontSize = selectedElementData.responseConfig?.optionFontSize || 14;
                      const optPadding = selectedElementData.responseConfig?.optionPadding || 8;
                      const optH = optFontSize + optPadding * 2 + 8;
                      const longestLabel = options.length > 0 ? Math.max(...options.map(o => (o.content || '').length)) : 5;
                      const estOptW = Math.min(parentW, longestLabel * optFontSize * 0.65 + 50);

                      const applySubElements = (updated) => {
                        const updatedScreens = screens.map((screen, idx) => {
                          if (idx !== currentScreenIndex) return screen;
                          return { ...screen, elements: screen.elements.map(el => el.id === selectedElement ? { ...el, subElements: updated } : el) };
                        });
                        setScreens(updatedScreens);
                      };

                      const alignOptionsToAnchor = (anchorX, anchorY) => {
                        if (options.length === 0) return;
                        // Compute bounding box of options as they currently are
                        const minX = Math.min(...options.map(o => o.position.x));
                        const minY = Math.min(...options.map(o => o.position.y));
                        const maxX = Math.max(...options.map(o => o.position.x + (o.content || '').length * optFontSize * 0.7 + 50));
                        const maxY = Math.max(...options.map(o => o.position.y + optH));
                        const groupW = maxX - minX;
                        const groupH = maxY - minY;

                        // Target position for the group's top-left corner
                        const targetX = anchorX === 'left' ? 0 : anchorX === 'center' ? (parentW - groupW) / 2 : parentW - groupW;
                        const targetY = anchorY === 'top' ? 0 : anchorY === 'middle' ? (parentH - groupH) / 2 : parentH - groupH;

                        // Translate all options by the delta
                        const dx = targetX - minX;
                        const dy = targetY - minY;
                        const updated = selectedElementData.subElements.map(sub => {
                          if (sub.type !== 'question-option') return sub;
                          return { ...sub, position: { x: Math.max(0, Math.round(sub.position.x + dx)), y: Math.max(0, Math.round(sub.position.y + dy)) } };
                        });
                        applySubElements(updated);
                      };

                      const cellClass = "group flex h-6 w-6 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white transition-colors hover:border-primary-400 hover:bg-primary-50 dark:border-white/10 dark:bg-gray-900 dark:hover:border-primary-400 dark:hover:bg-primary-400/10";

                      return (
                        <>
                          {/* Question Text position */}
                          {textSub && (
                            <div className="mt-2">
                              <div className="mb-1 text-xs font-medium text-gray-600 dark:text-gray-400">Question Text</div>
                              <div className="flex items-center gap-1.5">
                                <span className="w-4 text-xs text-gray-500 dark:text-gray-400">X</span>
                                <NumericInput value={Math.round(textSub.position.x)} fallback={0} onChange={(val) => updateSubElementPosition(selectedElement, textSub.id, { ...textSub.position, x: val })} className="input h-8 w-14 px-1.5 text-center text-xs tabular-nums" />
                                <span className="w-4 text-xs text-gray-500 dark:text-gray-400">Y</span>
                                <NumericInput value={Math.round(textSub.position.y)} fallback={0} onChange={(val) => updateSubElementPosition(selectedElement, textSub.id, { ...textSub.position, y: val })} className="input h-8 w-14 px-1.5 text-center text-xs tabular-nums" />
                              </div>
                            </div>
                          )}

                          {/* Response options alignment */}
                          {options.length > 0 && (
                            <div className="mt-2">
                              <div className="mb-1 text-xs font-medium text-gray-600 dark:text-gray-400">Align Response Options</div>
                              <div className="flex items-start gap-3">
                                <div className="grid grid-cols-3 gap-0.5 rounded-lg border border-gray-200/70 bg-gray-50 dark:border-white/[0.07] dark:bg-white/[0.03] p-1">
                                  {['top','middle','bottom'].map(row =>
                                    ['left','center','right'].map(col => (
                                      <button key={`${row}-${col}`} onClick={() => alignOptionsToAnchor(col, row)} className={cellClass} title={`${row} ${col}`}>
                                        <div className="h-1.5 w-1.5 rounded-full bg-gray-400 group-hover:bg-primary-600 dark:bg-gray-500 dark:group-hover:bg-primary-400" />
                                      </button>
                                    ))
                                  )}
                                </div>
                                <div className="flex flex-col gap-1.5 flex-1">
                                  <button onClick={() => distributeOptions(selectedElement, 'vertical')} className="btn btn-secondary btn-sm justify-start px-2 text-xs">
                                    <ArrowUpDown className="h-3.5 w-3.5 flex-shrink-0" /> Stack Vertical
                                  </button>
                                  <button onClick={() => distributeOptions(selectedElement, 'horizontal')} className="btn btn-secondary btn-sm justify-start px-2 text-xs">
                                    <ArrowLeftRight className="h-3.5 w-3.5 flex-shrink-0" /> Stack Horizontal
                                  </button>
                                  <button onClick={() => distributeOptions(selectedElement, 'grid')} className="btn btn-secondary btn-sm justify-start px-2 text-xs">
                                    <Layout className="h-3.5 w-3.5 flex-shrink-0" /> Grid (2 col)
                                  </button>
                                </div>
                              </div>

                              {/* Per-option position list */}
                              <div className="mt-2">
                                <div className="mb-1 text-xs font-medium text-gray-600 dark:text-gray-400">Option Positions</div>
                                <div className="space-y-1 max-h-40 overflow-y-auto">
                                  {options.map((sub) => (
                                    <div key={sub.id} className="flex items-center gap-1.5 text-xs">
                                      <span className="w-16 truncate text-gray-600 dark:text-gray-400" title={sub.content}>{sub.content}</span>
                                      <NumericInput value={Math.round(sub.position.x)} fallback={0} onChange={(val) => updateSubElementPosition(selectedElement, sub.id, { ...sub.position, x: val })} className="input h-8 w-12 px-1 text-center text-xs tabular-nums" title="X" />
                                      <NumericInput value={Math.round(sub.position.y)} fallback={0} onChange={(val) => updateSubElementPosition(selectedElement, sub.id, { ...sub.position, y: val })} className="input h-8 w-12 px-1 text-center text-xs tabular-nums" title="Y" />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </>
                ) : (
                  <button
                    onClick={() => ungroupQuestion(selectedElement)}
                    className="btn btn-secondary btn-sm w-full"
                  >
                    <MousePointer className="h-3.5 w-3.5" />
                    Custom Layout
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Element Code - For input elements */}
      {isInputElement && (
        <div className="card p-4">
          <div className="mb-3 flex items-center gap-2">
            <Type className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <h4 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Element Identifier</h4>
          </div>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Code / Variable Name</label>
              <input
                type="text"
                value={selectedElementData.code || ''}
                onChange={(e) => handleElementCodeChange(e.target.value)}
                placeholder="e.g., pain_level, comments"
                className="input h-9 font-mono text-[13px]"
              />
              <p className="hint">
                <span className="font-medium text-gray-700 dark:text-gray-200">This is what shows in exports.</span> Leave blank to use the internal ID below.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200/70 bg-gray-50 dark:border-white/[0.07] dark:bg-white/[0.03] px-3 py-2.5">
              <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">Internal Element ID (read-only)</label>
              <code className="code-chip max-w-full break-all">{selectedElementData.id}</code>
            </div>
          </div>
        </div>
      )}

      {/* Input Element Properties - Label, Placeholder */}
      {isInputElement && (
        <div className="card p-4">
          <div className="mb-3 flex items-center gap-2">
            <Edit2 className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <h4 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Input Properties</h4>
          </div>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Label</label>
              <input
                type="text"
                value={selectedElementData.label || ''}
                onChange={(e) => handleInputPropertyChange('label', e.target.value)}
                placeholder="Enter label text..."
                className="input h-9"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Placeholder</label>
              <input
                type="text"
                value={selectedElementData.placeholder || ''}
                onChange={(e) => handleInputPropertyChange('placeholder', e.target.value)}
                placeholder="Enter placeholder text..."
                className="input h-9"
              />
            </div>
            {selectedElementData.type === 'number-input' && (
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Min</label>
                  <NumericInput
                    value={selectedElementData.config?.min ?? ''}
                    fallback={null}
                    onChange={(val) => handleInputPropertyChange('config', { ...selectedElementData.config, min: val })}
                    placeholder="Min"
                    className="input h-9 px-2 tabular-nums"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Max</label>
                  <NumericInput
                    value={selectedElementData.config?.max ?? ''}
                    fallback={null}
                    onChange={(val) => handleInputPropertyChange('config', { ...selectedElementData.config, max: val })}
                    placeholder="Max"
                    className="input h-9 px-2 tabular-nums"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Step</label>
                  <NumericInput
                    value={selectedElementData.config?.step ?? 1}
                    fallback={1}
                    onChange={(val) => handleInputPropertyChange('config', { ...selectedElementData.config, step: val || 1 })}
                    placeholder="Step"
                    className="input h-9 px-2 tabular-nums"
                  />
                </div>
              </div>
            )}
            {selectedElementData.type === 'text-input' && (
              <div className="space-y-2">
                <ToggleRow
                  title="Multi-line (textarea)"
                  checked={selectedElementData.config?.multiline || false}
                  onChange={(e) => handleInputPropertyChange('config', { ...selectedElementData.config, multiline: e.target.checked })}
                />
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Max Length</label>
                  <NumericInput
                    value={selectedElementData.config?.maxLength ?? ''}
                    fallback={null}
                    onChange={(val) => handleInputPropertyChange('config', { ...selectedElementData.config, maxLength: val })}
                    placeholder="No limit"
                    className="input h-9 tabular-nums"
                  />
                </div>
              </div>
            )}
            <ToggleRow
              title="Required"
              checked={selectedElementData.config?.required || selectedElementData.required || false}
              onChange={(e) => handleInputPropertyChange('config', { ...selectedElementData.config, required: e.target.checked })}
            />
          </div>
        </div>
      )}


      {/* Element Position on Canvas */}
      {(() => {
        const canvasLayout = screens[currentScreenIndex]?.layout || {};
        const canvasW = canvasLayout.width || 810;
        const canvasH = canvasLayout.height || 1080;
        const elW = selectedElementData.size?.width || 100;
        const elH = selectedElementData.size?.height || 100;

        const alignElement = (anchorX, anchorY) => {
          const x = anchorX === 'left' ? 0 : anchorX === 'center' ? (canvasW - elW) / 2 : canvasW - elW;
          const y = anchorY === 'top' ? 0 : anchorY === 'middle' ? (canvasH - elH) / 2 : canvasH - elH;
          const updatedScreens = screens.map((screen, idx) => {
            if (idx !== currentScreenIndex) return screen;
            return {
              ...screen,
              elements: screen.elements.map(el =>
                el.id === selectedElement
                  ? { ...el, position: { x: Math.max(0, Math.round(x)), y: Math.max(0, Math.round(y)) } }
                  : el
              )
            };
          });
          setScreens(updatedScreens);
        };

        const cellClass = "group flex h-6 w-6 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white transition-colors hover:border-primary-400 hover:bg-primary-50 dark:border-white/10 dark:bg-gray-900 dark:hover:border-primary-400 dark:hover:bg-primary-400/10";

        return (
          <div className="card p-4">
            <div className="mb-3 flex items-center gap-2">
              <Layout className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
              <h4 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Position on Canvas</h4>
            </div>
            <div className="flex items-center gap-3">
              <div className="grid grid-cols-3 gap-0.5 rounded-lg border border-gray-200/70 bg-gray-50 dark:border-white/[0.07] dark:bg-white/[0.03] p-1">
                {['top','middle','bottom'].map(row =>
                  ['left','center','right'].map(col => (
                    <button
                      key={`el-${row}-${col}`}
                      onClick={() => alignElement(col, row)}
                      className={cellClass}
                      title={`${row} ${col}`}
                    >
                      <div className="h-1.5 w-1.5 rounded-full bg-gray-400 group-hover:bg-primary-600 dark:bg-gray-500 dark:group-hover:bg-primary-400" />
                    </button>
                  ))
                )}
              </div>
              <div className="flex-1 space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <span className="w-4 text-xs font-medium text-gray-500 dark:text-gray-400">X</span>
                  <NumericInput
                    value={Math.round(selectedElementData.position?.x || 0)}
                    fallback={0}
                    onChange={(val) => {
                      const updatedScreens = screens.map((screen, idx) => {
                        if (idx !== currentScreenIndex) return screen;
                        return { ...screen, elements: screen.elements.map(el => el.id === selectedElement ? { ...el, position: { ...el.position, x: val } } : el) };
                      });
                      setScreens(updatedScreens);
                    }}
                    className="input h-8 w-full min-w-0 px-1.5 text-center text-xs tabular-nums"
                  />
                  <span className="w-4 text-xs font-medium text-gray-500 dark:text-gray-400">Y</span>
                  <NumericInput
                    value={Math.round(selectedElementData.position?.y || 0)}
                    fallback={0}
                    onChange={(val) => {
                      const updatedScreens = screens.map((screen, idx) => {
                        if (idx !== currentScreenIndex) return screen;
                        return { ...screen, elements: screen.elements.map(el => el.id === selectedElement ? { ...el, position: { ...el.position, y: val } } : el) };
                      });
                      setScreens(updatedScreens);
                    }}
                    className="input h-8 w-full min-w-0 px-1.5 text-center text-xs tabular-nums"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-4 text-xs font-medium text-gray-500 dark:text-gray-400">W</span>
                  <NumericInput
                    value={Math.round(selectedElementData.size?.width || 0)}
                    fallback={50}
                    min={50}
                    onChange={(val) => {
                      const updatedScreens = screens.map((screen, idx) => {
                        if (idx !== currentScreenIndex) return screen;
                        return { ...screen, elements: screen.elements.map(el => el.id === selectedElement ? { ...el, size: { ...el.size, width: Math.max(50, val) } } : el) };
                      });
                      setScreens(updatedScreens);
                    }}
                    className="input h-8 w-full min-w-0 px-1.5 text-center text-xs tabular-nums"
                  />
                  <span className="w-4 text-xs font-medium text-gray-500 dark:text-gray-400">H</span>
                  <NumericInput
                    value={Math.round(selectedElementData.size?.height || 0)}
                    fallback={30}
                    min={30}
                    onChange={(val) => {
                      const updatedScreens = screens.map((screen, idx) => {
                        if (idx !== currentScreenIndex) return screen;
                        return { ...screen, elements: screen.elements.map(el => el.id === selectedElement ? { ...el, size: { ...el.size, height: Math.max(30, val) } } : el) };
                      });
                      setScreens(updatedScreens);
                    }}
                    className="input h-8 w-full min-w-0 px-1.5 text-center text-xs tabular-nums"
                  />
                </div>
              </div>
            </div>

          </div>
        );
      })()}

      {/* Button Behavior - Action, Target Screen */}
      {selectedElementData.type === 'button' && (
        <div className="card p-4">
          <div className="mb-3 flex items-center gap-2">
            <MousePointer className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <h4 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Button Behavior</h4>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Action</label>
              <select 
                className="input h-9"
                value={resolveSurveyButtonAction(selectedElementData)}
                onChange={(e) => {
                  const newAction = e.target.value;
                  
                  // Map actions to default style presets
                  const actionPresetMap = {
                    next: 'primary',
                    back: 'secondary',
                    jump: 'primary',
                    submit: 'success',
                    skip: 'outline'
                  };
                  
                  // Map actions to default button text
                  const actionTextMap = {
                    next: 'Next',
                    back: 'Back',
                    jump: 'Continue',
                    submit: 'Submit',
                    skip: 'Next'
                  };
                  
                  const presetKey = actionPresetMap[newAction] || 'primary';
                  const preset = BUTTON_STYLE_PRESETS[presetKey];
                  const defaultText = actionTextMap[newAction] || 'Button';
                  
                  const updatedScreens = screens.map((screen, idx) => {
                    if (idx === currentScreenIndex) {
                      return {
                        ...screen,
                        elements: screen.elements.map(el =>
                          el.id === selectedElement
                            ? { 
                                ...applySurveyButtonAction(el, newAction),
                                content: defaultText,
                                label: defaultText,
                                style: {
                                  ...(el.style || {}),
                                  backgroundColor: preset.backgroundColor,
                                  textColor: preset.textColor,
                                  borderRadius: preset.borderRadius,
                                  borderWidth: preset.borderWidth,
                                  borderColor: preset.borderColor,
                                  preset: presetKey
                                }
                              }
                            : el
                        )
                      };
                    }
                    return screen;
                  });
                  setScreens(updatedScreens);
                }}
              >
                <option value="next">Next Screen (follow defaults)</option>
                <option value="back">Previous Screen</option>
                <option value="jump">Jump to Specific Screen</option>
                <option value="submit">Submit Survey</option>
                <option value="skip">Custom (use skip logic)</option>
              </select>
            </div>

            {selectedElementData.action === 'jump' && (
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Target Screen</label>
                <select
                  className="input h-9"
                  value={selectedElementData.targetScreen || ''}
                  onChange={(e) => {
                    const updatedScreens = screens.map((screen, idx) => {
                      if (idx === currentScreenIndex) {
                        return {
                          ...screen,
                          elements: screen.elements.map(el =>
                            el.id === selectedElement
                              ? { ...el, targetScreen: e.target.value }
                              : el
                          )
                        };
                      }
                      return screen;
                    });
                    setScreens(updatedScreens);
                  }}
                >
                  <option value="">Select a screen...</option>
                  {screens.map((screen, idx) => (
                    <option key={screen.id} value={screen.id}>
                      {screen.name || `Screen ${idx + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedElementData.action === 'submit' && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-500/30 dark:bg-emerald-500/10">
                <div className="flex items-start gap-2">
                  <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
                  <p className="text-[12.5px] leading-relaxed text-emerald-700 dark:text-emerald-300">
                    This button will complete the survey immediately.
                  </p>
                </div>
              </div>
            )}

            {selectedElementData.action === 'skip' && (
              <div className="flex items-start gap-2 rounded-lg border border-gray-200/70 bg-gray-50 dark:border-white/[0.07] dark:bg-white/[0.03] p-3">
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                <p className="text-[12.5px] leading-relaxed text-gray-600 dark:text-gray-300">
                  Configure skip logic rules in the <strong className="font-semibold text-gray-800 dark:text-gray-100">Navigation</strong> tab.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Button Appearance - Styling controls */}
      {selectedElementData.type === 'button' && (
        <div className="card p-4">
          <div className="mb-3 flex items-center gap-2">
            <Palette className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            <h4 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">Button Appearance</h4>
          </div>
          
          <div className="space-y-4">
            {/* Platform/Theme Dropdown */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Button Theme</label>
              <select
                className="input h-9"
                value={selectedElementData.style?.theme || 'default'}
                onChange={(e) => applyThemePreset(e.target.value)}
              >
                {Object.entries(BUTTON_THEME_PRESETS).map(([key, theme]) => (
                  <option key={key} value={key}>
                    {theme.name} - {theme.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Style Presets */}
            <div>
              <label className="mb-1.5 block text-xs font-medium text-gray-600 dark:text-gray-400">Quick Styles</label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(BUTTON_STYLE_PRESETS).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => applyStylePreset(key)}
                    className={`rounded-lg border px-2 py-1.5 text-[11.5px] font-medium leading-tight transition-[border-color,box-shadow] duration-150 ${
                      selectedElementData.style?.preset === key
                        ? 'border-primary-500 ring-2 ring-primary-500 ring-offset-2 ring-offset-white dark:border-primary-400 dark:ring-primary-400 dark:ring-offset-gray-800'
                        : 'border-gray-200 hover:border-gray-300 dark:border-white/10 dark:hover:border-white/20'
                    }`}
                    style={{
                      backgroundColor: preset.backgroundColor,
                      color: preset.textColor,
                      borderRadius: `${Math.min(parseInt(preset.borderRadius), 12)}px`
                    }}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Color Controls */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Background</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={selectedElementData.style?.backgroundColor || '#0d9488'}
                    onChange={(e) => updateButtonStyle('backgroundColor', e.target.value)}
                    className="h-9 w-9 flex-shrink-0 cursor-pointer rounded-lg border border-gray-300/80 dark:border-white/10"
                  />
                  <input
                    type="text"
                    value={selectedElementData.style?.backgroundColor || '#0d9488'}
                    onChange={(e) => updateButtonStyle('backgroundColor', e.target.value)}
                    className="input h-9 min-w-0 flex-1 px-2 font-mono text-xs"
                    placeholder="#0d9488"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Text Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={selectedElementData.style?.textColor || '#ffffff'}
                    onChange={(e) => updateButtonStyle('textColor', e.target.value)}
                    className="h-9 w-9 flex-shrink-0 cursor-pointer rounded-lg border border-gray-300/80 dark:border-white/10"
                  />
                  <input
                    type="text"
                    value={selectedElementData.style?.textColor || '#ffffff'}
                    onChange={(e) => updateButtonStyle('textColor', e.target.value)}
                    className="input h-9 min-w-0 flex-1 px-2 font-mono text-xs"
                    placeholder="#ffffff"
                  />
                </div>
              </div>
            </div>

            {/* Border Controls */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Border Radius</label>
                <div className="flex items-center gap-1">
                  <NumericInput
                    min={0}
                    max={9999}
                    value={selectedElementData.style?.borderRadius || '8'}
                    fallback={8}
                    onChange={(val) => updateButtonStyle('borderRadius', String(val))}
                    className="input h-9 min-w-0 px-2 tabular-nums"
                  />
                  <span className="text-xs text-gray-500 dark:text-gray-400">px</span>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Border Width</label>
                <div className="flex items-center gap-1">
                  <NumericInput
                    min={0}
                    max={10}
                    value={selectedElementData.style?.borderWidth || '0'}
                    fallback={0}
                    onChange={(val) => updateButtonStyle('borderWidth', String(val))}
                    className="input h-9 min-w-0 px-2 tabular-nums"
                  />
                  <span className="text-xs text-gray-500 dark:text-gray-400">px</span>
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Border Color</label>
                <input
                  type="color"
                  value={selectedElementData.style?.borderColor || '#0d9488'}
                  onChange={(e) => updateButtonStyle('borderColor', e.target.value)}
                  className="h-9 w-full cursor-pointer rounded-lg border border-gray-300/80 dark:border-white/10"
                />
              </div>
            </div>

            {/* Live Preview */}
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Preview</label>
              <div className="flex items-center justify-center rounded-lg border border-gray-200/70 bg-gray-50 dark:border-white/[0.07] dark:bg-white/[0.03] p-4">
                <button
                  className="px-4 py-2 font-medium transition-all"
                  style={{
                    backgroundColor: selectedElementData.style?.backgroundColor || '#0d9488',
                    color: selectedElementData.style?.textColor || '#ffffff',
                    borderRadius: `${selectedElementData.style?.borderRadius || 8}px`,
                    borderWidth: `${selectedElementData.style?.borderWidth || 0}px`,
                    borderStyle: 'solid',
                    borderColor: selectedElementData.style?.borderColor || '#0d9488'
                  }}
                >
                  {selectedElementData.content || selectedElementData.label || 'Button'}
                </button>
              </div>
            </div>

            {/* Advanced CSS - Collapsible */}
            <div className="border-t border-gray-100 pt-3 dark:border-white/[0.06]">
              <button
                onClick={() => setShowAdvancedCss(!showAdvancedCss)}
                className="flex w-full items-center justify-between rounded-md text-left text-gray-700 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
              >
                <div className="flex items-center gap-2">
                  <Code className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <span className="text-[13px] font-medium">Advanced CSS</span>
                </div>
                {showAdvancedCss ? (
                  <ChevronUp className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                )}
              </button>
              
              {showAdvancedCss && (
                <div className="mt-2">
                  <textarea
                    value={selectedElementData.style?.customCss || ''}
                    onChange={(e) => updateButtonStyle('customCss', e.target.value)}
                    placeholder="/* Custom CSS */&#10;box-shadow: 0 4px 6px rgba(0,0,0,0.1);&#10;text-transform: uppercase;"
                    className="input resize-none font-mono text-xs"
                    rows={4}
                  />
                  <p className="hint">
                    Enter custom CSS properties. These will override the controls above.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Element actions */}
      <div className="space-y-2 border-t border-gray-100 pt-4 dark:border-white/[0.06]">
        <button
          onClick={() => toggleLockElement(selectedElement)}
          className={`btn btn-sm w-full ${
            isElementLocked(selectedElement)
              ? 'border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200 dark:hover:bg-amber-500/15'
              : 'btn-secondary'
          }`}
        >
          {isElementLocked(selectedElement) ? (
            <>
              <Lock className="h-3.5 w-3.5" />
              Unlock
            </>
          ) : (
            <>
              <Unlock className="h-3.5 w-3.5" />
              Lock
            </>
          )}
        </button>
        <button
          onClick={() => deleteElement(selectedElement)}
          className="btn btn-danger-soft btn-sm w-full border border-red-200 dark:border-red-500/30"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Delete Element
        </button>
      </div>
    </div>
  );
};

export default PropertiesPanel;
